import React, { useCallback, useMemo } from 'react';
import { IOnSubmitArgs, OpForm } from "components/customAntd/DLS/OpForm/OpForm";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store/store";
import { updateVisitorWorkflow, updateVisitorWorkflowConfig } from 'store/slices/visitorWorkflowsSlice';
import { List } from 'antd';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpRow } from 'components/customAntd/DLS/OpRow/OpRow';
import { OpCol } from 'components/customAntd/DLS/OpCol/OpCol';
import locationIcon from '../../location.svg';
import { hasPermission } from 'utils/utils';
import { useTranslation } from 'react-i18next';

interface WorkflowContentProps {
    form: any;
}

const WorkflowContent: React.FC<WorkflowContentProps> = ({ form }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisitorWorkflow, selectedVisitorWorkflowLocations: visitorWorkflowLocations, selectedSignInWorkflowConfig: visitorWorkflowConfig } = useSelector((state: RootState) => state.visitorWorkflows);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const hasSignoutconfRead = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:r');
    const hasSignoutconfWrite = hasPermission(tokenScopeList, orgId, 'o', 'signoutconf:w');
    const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode); // Accessing isDarkMode from Redux

    const { t } = useTranslation();

    const confirmReturnItemsData = [
        { name: 'confirmReturnItems', label: t('workflow.attendantSignOut.form.confirmReturnItems'), valuePropName: 'checked' },
    ];

    const notificationData = [
        { name: 'notifyHost', label: t('workflow.attendantSignOut.form.notifyHost'), valuePropName: 'checked' },
    ];

    const initialValues = useMemo(() => {
        if (selectedVisitorWorkflow) {
            return {
                name: selectedVisitorWorkflow.name,
                workflowType: t('workflow.attendantSignOut.workflowType'),
                confirmReturnItems: visitorWorkflowConfig?.confirmReturnItems,
                notifyHost: visitorWorkflowConfig?.notifyHost,
            };
        }
        return {};
    }, [selectedVisitorWorkflow, visitorWorkflowConfig, t]);

    const handleSubmit = useCallback(({ values, touchedValues, initialValues }: IOnSubmitArgs<any>) => {
        const visitorWorkflowConfigParams = {
            confirmReturnItems: values.confirmReturnItems ? 1 : 0,
            notifyHost: values.notifyHost ? 1 : 0,
        };

        if (selectedVisitorWorkflow) {
            dispatch(updateVisitorWorkflow({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflow: { name: touchedValues.name } }));
            dispatch(updateVisitorWorkflowConfig({ orgId, visitorWorkflowId: selectedVisitorWorkflow.id, visitorWorkflowConfigParams }));
        }
    }, [dispatch, orgId, selectedVisitorWorkflow]);


    const locationIconStyle = {
        marginRight: '8px',
        width: '16px',
        height: '16px',
        filter: isDarkMode ? 'invert(1)' : 'none',
    };

    return (
        <OpForm
            form={form}
            initialValues={initialValues}
            onSubmit={handleSubmit}
            hasError={false}
            defaultButtons={false}
            isReadOnly={!hasSignoutconfWrite && hasSignoutconfRead}
        >
            <OpSpace
                direction="vertical"
                size="middle"
                style={{
                    display: 'flex',
                }}
            >
                <OpRow gutter={16}>
                    <OpCol span={12}>
                        <OpForm.Input label={t('workflow.attendantSignOut.form.name')} name="name" rules={[{ required: true, message: t('workflow.attendantSignOut.form.nameRequired') }]} />
                    </OpCol>
                    <OpCol span={12}>
                        <OpForm.Input disabled label={t('workflow.attendantSignOut.form.workflowType')} name="workflowType" />
                    </OpCol>
                </OpRow>

                <List
                    header={t('workflow.attendantSignOut.form.checkListTitle')}
                    dataSource={confirmReturnItemsData}
                    bordered
                    renderItem={item => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <OpForm.Switch
                                    name={item.name} style={{ marginBottom: 0 }}
                                    disabled={!hasSignoutconfWrite && hasSignoutconfRead} />
                                <span style={{ marginLeft: 8 }}>{item.label}</span>
                            </div>
                        </List.Item>
                    )} />

                <List
                    header={t('workflow.attendantSignOut.form.notificationTitle')}
                    dataSource={notificationData}
                    bordered
                    renderItem={item => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <OpForm.Switch name={item.name} style={{ marginBottom: 0 }}
                                    disabled={!hasSignoutconfWrite && hasSignoutconfRead} />
                                <span style={{ marginLeft: 8 }}>{item.label}</span>
                            </div>
                        </List.Item>
                    )} />

                <List
                    header={t('workflow.attendantSignOut.form.locationsTitle')}
                    bordered
                    dataSource={visitorWorkflowLocations.data.filter(location => location.status === 1)}
                    renderItem={location => (
                        <List.Item>
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                <img src={locationIcon} alt="location icon" style={locationIconStyle} />
                                <span>{location.name}</span>
                            </div>
                        </List.Item>
                    )}
                />
            </OpSpace>
        </OpForm>
    );
};

export default WorkflowContent;
